import { useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import {
  deleteDevicePhoto,
  getDevicePhotos,
  orderDevicePhotos,
  uploadDevicePhoto,
} from '@/features/operations/api';
import type { DevicePhoto } from '@/features/operations/types';

export function DevicePhotoManager({ token, deviceId }: { token: string; deviceId: string }) {
  const [photos, setPhotos] = useState<DevicePhoto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = async () => setPhotos(await getDevicePhotos(token, deviceId));
  useEffect(() => {
    refresh().catch((e: unknown) =>
      setError(e instanceof Error ? e.message : 'No pudimos cargar las fotos.'),
    );
  }, [token, deviceId]);
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos guardar las fotos.');
    } finally {
      setBusy(false);
    }
  };
  const move = (index: number, delta: number) => {
    const next = [...photos];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    return run(() =>
      orderDevicePhotos(
        token,
        deviceId,
        next.map((photo) => photo.id),
      ),
    );
  };
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Fotos para el catálogo</Typography>
      <Typography variant="caption" color="text.secondary">
        Fotos propias del equipo. JPG, PNG o WEBP; máximo 5 MB por foto.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {photos.map((photo, index) => (
          <Box key={photo.id} sx={{ width: 132 }}>
            <Box
              component="img"
              src={photo.url}
              alt={`Foto de catálogo ${index + 1}`}
              sx={{ width: 132, height: 100, objectFit: 'cover', borderRadius: 1 }}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption">{index + 1}</Typography>
              <IconButton
                size="small"
                aria-label={`Mover foto ${index + 1} antes`}
                disabled={busy || index === 0}
                onClick={() => move(index, -1)}
              >
                ←
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Mover foto ${index + 1} después`}
                disabled={busy || index === photos.length - 1}
                onClick={() => move(index, 1)}
              >
                →
              </IconButton>
              <Button
                size="small"
                color="error"
                disabled={busy}
                onClick={() => run(() => deleteDevicePhoto(token, deviceId, photo.id))}
              >
                Borrar
              </Button>
            </Stack>
          </Box>
        ))}
      </Stack>
      <Button component="label" variant="outlined" disabled={busy} sx={{ alignSelf: 'flex-start' }}>
        Subir foto
        <input
          hidden
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void run(() => uploadDevicePhoto(token, deviceId, file));
            event.target.value = '';
          }}
        />
      </Button>
    </Stack>
  );
}
